<template>
  <div
    class="ic-monitoring-updates"
    :class="{ 'ic-monitoring-updates--error': hasError }"
  >
    <h3 class="ic-monitoring-updates__title">Latest Monitoring Updates</h3>
    <template v-if="hasMonitoredReports && latestMonitoringUpdates?.length">
      <button
        v-for="report in latestMonitoringUpdates"
        :key="report.id"
        class="ic-monitoring-updates__report"
        @click="
          openReport({
            reportType: report.report_type,
            reportCode: report.report_code,
            reportMonitorId: report.id
          })
        "
        v-html="
          updatesAvailableCopy({
            changes: report.changes,
            target: report.report_target
          })
        "
      />
      <a class="ic-monitoring-updates__button" :href="monitorReportsUrl">
        View All Updates
      </a>
    </template>
    <!-- Display message when there's no updates available, no monitored reports, or an error occurs -->
    <div
      v-if="!hasMonitoredReports || !latestMonitoringUpdates?.length"
      class="ic-monitoring-updates__wrapper"
    >
      <h4 class="ic-monitoring-updates__subtitle">{{ message.title }}</h4>
      <p class="ic-monitoring-updates__body">{{ message.body }}</p>
    </div>
  </div>
</template>

<script>
// Helpers
import pluralize from '@/assets/js/shared/helpers/pluralize';
// Store
import { mapGetters } from 'vuex';

export default {
  name: 'ic-monitoring-updates',
  inject: {
    openReport: {
      default: () => () => {
        console.warn('openReport not provided');
        return '';
      }
    }
  },
  computed: {
    ...mapGetters('report-monitor', [
      'latestMonitoringUpdates',
      'reportsMonitored',
      'hasError'
    ]),
    monitorReportsUrl() {
      const { href } = this.$router.resolve({ name: 'dashboard-monitor' });
      return href;
    },
    hasMonitoredReports() {
      return !this.hasError && this.reportsMonitored > 0;
    },
    message() {
      if (this.hasError) {
        return {
          title: 'Unable to Load Reports',
          body:
            'We ran into an issue while loading your monitored reports. Refresh the page or try again shortly.'
        };
      }

      if (this.hasMonitoredReports && !this.latestMonitoringUpdates?.length) {
        return {
          title: 'No Updates Available',
          body:
            "When a monitored report is updated, you'll see the changes here."
        };
      }

      if (!this.hasMonitoredReports) {
        return {
          title: 'You are not monitoring any reports',
          body:
            'To start, simply turn ON the "Monitor Report" toggle on the report you wish to track.'
        };
      }

      return null;
    }
  },
  methods: {
    /**
     * Gets copy for when a report has updates available
     *
     * @example
     * getUpdatesAvailableCopy({changes: {phone: 1, address: 1}, target: 'William A Smith'})
     * // => "2 new updates available for <strong>William A Smith</strong>"
     * getUpdatesAvailableCopy({changes: {addresses: 1}, target: '(123) 456-7890'})
     * // => "1 new update available for <strong>(123) 456-7890</strong>"
     *
     * @param {object} opts
     * @param {object} opts.changes - Object containing the number of updates
     * @param {string} opts.target - String to display in the copy (e.g. "William A Smith", "(123) 456-7890")
     * @returns {string}
     * @throws {TypeError} If changes is not an object or target is not a string
     */
    updatesAvailableCopy({ changes, target } = {}) {
      if (!changes || typeof changes !== 'object') {
        throw new TypeError('changes is required and must be an object');
      }

      if (!target || typeof target !== 'string') {
        throw new TypeError('target is required and must be a string');
      }
      let count = 0;
      for (const value of Object.values(changes)) {
        if (typeof value === 'number' && Number.isFinite(value)) {
          count += value;
        }
      }
      // Format the count to be a compact number
      const formattedCount = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short'
      }).format(count);
      const updateWord = pluralize({
        count,
        singular: 'update',
        plural: 'updates'
      });

      return `${formattedCount} new ${updateWord} available for <strong>${target}</strong>`;
    }
  }
};
</script>
